import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress, Grid2 } from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { DrasticEppnaForm as DrasticEppnaFormType, getDrasticEppnaFormSchema } from "../../../types/forms/capture";
import { VulnerabilitySelect } from "../VulnerabilitySelect";

interface DrasticEppnaFormProps {
  drasticEppnaInputs?: DrasticEppnaFormType | null;
  canEdit: boolean;
  onSubmit: (values: DrasticEppnaFormType) => Promise<void>;
}

export default function DrasticEppnaForm({
  onSubmit,
  canEdit,
  drasticEppnaInputs,
}: Readonly<DrasticEppnaFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(false);

  const validationSchema = getDrasticEppnaFormSchema(t);

  const methods = useForm<DrasticEppnaFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      drasticDId: drasticEppnaInputs?.drasticDId ?? undefined,
      drasticRId: drasticEppnaInputs?.drasticRId ?? undefined,
      drasticAId: drasticEppnaInputs?.drasticAId ?? undefined,
      drasticSId: drasticEppnaInputs?.drasticSId ?? undefined,
      drasticTId: drasticEppnaInputs?.drasticTId ?? undefined,
      drasticIId: drasticEppnaInputs?.drasticIId ?? undefined,
      drasticCId: drasticEppnaInputs?.drasticCId ?? undefined,
      eppnaId: drasticEppnaInputs?.eppnaId ?? undefined,
    },
  });

  useEffect(() => {
    if (drasticEppnaInputs) {
      methods.reset(drasticEppnaInputs);
    }
  }, [drasticEppnaInputs, methods]);

  const { control } = methods;

  const { data: drasticD = [] } = useReferenceData("drastic", "drastic_d");
  const { data: drasticR = [] } = useReferenceData("drastic", "drastic_r");
  const { data: drasticA = [] } = useReferenceData("drastic", "drastic_a");
  const { data: drasticS = [] } = useReferenceData("drastic", "drastic_s");
  const { data: drasticT = [] } = useReferenceData("drastic", "drastic_t");
  const { data: drasticI = [] } = useReferenceData("drastic", "drastic_i");
  const { data: drasticC = [] } = useReferenceData("drastic", "drastic_c");
  const { data: eppna = [] } = useReferenceData("drastic", "eppna");


  const handleFormSubmit = async (values: DrasticEppnaFormType) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    } 
  };

  const buttonLabel = drasticEppnaInputs
    ? t("generic.recalculate")
    : t("generic.calculate");

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4} sx={{mb: 2}}>
            <VulnerabilitySelect
              name="drasticDId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-d")} *`}
              options={drasticD}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={4}>
            <VulnerabilitySelect 
              name="drasticRId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-r")} *`}
              options={drasticR}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={4}>
            <VulnerabilitySelect 
              name="drasticAId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-a")} *`}
              options={drasticA}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit} 
            />
          </Grid2>
          <Grid2 size={6} sx={{mb: 2}}>
            <VulnerabilitySelect 
              name="drasticSId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-s")} *`}
              options={drasticS}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={6}>
            <VulnerabilitySelect 
              name="drasticTId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-t")} *`}
              options={drasticT}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={6}>
            <VulnerabilitySelect 
              name="drasticIId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-i")} *`}
              options={drasticI}control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={6}>
            <VulnerabilitySelect 
              name="drasticCId" 
              label={`${t("calculations.vulnerability.drastic-eppna.drastic-c")} *`}
              options={drasticC}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={9} sx={{ mt: 5 }}>
            <VulnerabilitySelect 
              name="eppnaId" 
              label={`${t("calculations.vulnerability.drastic-eppna.eppna")} *`}
              options={eppna}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>

          {/* Action Bar */}
          {canEdit && (
            <Grid2 size={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -11 }}>
                <Button type="submit" variant="contained" sx={{ minWidth: 140, p: 1 }}>
                  {isSaving ? <CircularProgress size={24} /> : buttonLabel}
                </Button>
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Box>
    </FormProvider>
  );
}